import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import DataTable from '../../../../components/DataTable/DataTable.js';
import { GetIncidentDetails, GetComments, ToggleIncidentTrackerDialog, ToggleDeleteIncidentTrackerDialog } from '../../actions/IncidentTrackerActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const IncidentTrackerDatatable = (props) => {
	const { className, data, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const userId = useSelector(state => state.session.user.id);

	const _editIncident = (tableId) => {
		dispatch(GetIncidentDetails(userId, tableId));
		dispatch(GetComments(tableId));
		dispatch(ToggleIncidentTrackerDialog(true, tableId));
	};

	const _deleteIncident = (tableId) => {
		dispatch(ToggleDeleteIncidentTrackerDialog(true, tableId));
	};

	const _handleStyle = (value) => {
		if (value === 'OK') {
			return {
				style: {
					backgroundColor: 'green',
					color: 'white',
					fontWeight: 'bold'
				}
			};
		} if (value === 'NOK') {
			return {
				style: {
					backgroundColor: '#dc143c',
					color: 'white',
					fontWeight: 'bold'
				}
			};
		} if (value === 'N/A') {
			return {
				style: {
					backgroundColor: 'yellow',
					color: 'black',
					fontWeight: 'bold'
				}
			};
		}
		return {
		};
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<Tooltip title="Edit">
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						onClick={() => _editIncident(rowData[0])}
					>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Remove">
					<IconButton
						edge="end"
						size="small"
						className={classes.errorButton}
						onClick={() => _deleteIncident(rowData[0])}
					>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'siteId',
				label: 'Site Id',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'reportDateYear',
				label: 'Report Year',
				options: {
					display: true,
					filterType: 'textField'
				}
			},
			{
				name: 'reportDateWeek',
				label: 'Report Week',
				options: {
					display: true,
					filterType: 'textField'
				}
			},
			{
				name: 'date',
				label: 'Analysis Date',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'subRegion',
				label: 'Sub Region',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'city',
				label: 'City',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'category',
				label: 'Category',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'status',
				label: 'Status',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'owner',
				label: 'Owner',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'cfnOwner',
				label: 'RF Owner',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: false,
		expandableRows: false, // Try Adding This
	});

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="voiceDataTble"
				title=""
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={10}
				tableBodyMaxHeight="38em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

IncidentTrackerDatatable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default IncidentTrackerDatatable;
